/** Forgot password — requests a time-limited reset link (token is only ever delivered by email). */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Container, Section, Eyebrow, Button, Card, CardBody, AccentItalic } from "../../components/ui";
import { api } from "../../lib/api";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ stage: "idle" });
  const submit = async (e) => {
    e.preventDefault();
    setState({ stage: "loading" });
    try {
      const r = await api.post("/auth/forgot-password", { email });
      setState({ stage: "sent", emailConfigured: r.data?.email_configured !== false });
    } catch (err) {
      setState({ stage: "sent" });  // treat as success to avoid email enumeration
    }
  };

  return (
    <Section padded="sm">
      <Container className="max-w-md">
        <Eyebrow>Account</Eyebrow>
        <h1 className="font-display text-[clamp(28px,3.6vw,40px)] mt-3 leading-tight">
          Forgot <AccentItalic>password</AccentItalic>?
        </h1>
        <p className="text-text-dim text-[14px] mt-3 leading-relaxed">
          Enter the email on your NVL account. We&apos;ll send a one-time reset link to your inbox.
        </p>

        <Card variant="elevated" className="mt-8"><CardBody className="p-7">
          {state.stage === "sent" ? (
            <div data-testid="forgot-sent">
              <h3 className="font-display text-[20px]">Check your inbox</h3>
              <p className="text-text-dim text-[13.5px] mt-2">
                If an account exists for <b className="text-text">{email}</b>, a reset link will be sent.
                The link expires in 1 hour.
              </p>
              {!state.emailConfigured && (
                <p className="mt-4 text-[12.5px] text-text-faint" data-testid="forgot-email-not-configured">
                  Email delivery is being set up. If you don&apos;t receive a message, please <Link to="/contact" className="text-mauve hover:underline">contact us</Link> and our team will help you regain access.
                </p>
              )}
              <Button asChild variant="ghost" className="mt-6"><Link to="/login">Back to sign in</Link></Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <label className="block">
                <div className="text-[12px] font-mono uppercase tracking-[0.14em] text-text-dim mb-1">Email</div>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-surface border border-line-2 rounded-xl px-4 py-2.5 text-[14px] focus:outline-none focus:border-mauve"
                  data-testid="forgot-email" />
              </label>
              <Button type="submit" disabled={state.stage === "loading"} className="w-full justify-center" data-testid="forgot-submit">
                {state.stage === "loading" ? "Sending…" : "Send reset link"}
              </Button>
            </form>
          )}
        </CardBody></Card>
        <p className="text-[12.5px] text-text-faint mt-6 text-center">
          Remembered it? <Link to="/login" className="text-mauve hover:underline">Sign in</Link>
        </p>
      </Container>
    </Section>
  );
}

export function ResetPasswordPage() {
  const [token, setToken] = useState(() => new URLSearchParams(window.location.search).get("token") || "");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    try {
      await api.post("/auth/reset-password", { token, password });
      setMsg({ kind: "success", text: "Password updated. You may now sign in with the new password." });
    } catch (err) {
      setMsg({ kind: "error", text: err.response?.data?.detail || err.message });
    }
  };
  return (
    <Section padded="sm">
      <Container className="max-w-md">
        <Eyebrow>Account</Eyebrow>
        <h1 className="font-display text-[clamp(28px,3.6vw,40px)] mt-3 leading-tight">Set a new password</h1>
        <Card variant="elevated" className="mt-8"><CardBody className="p-7">
          <form onSubmit={submit} className="space-y-4">
            <label className="block">
              <div className="text-[12px] font-mono uppercase tracking-[0.14em] text-text-dim mb-1">Reset token</div>
              <input type="text" required value={token} onChange={(e) => setToken(e.target.value)}
                className="w-full bg-surface border border-line-2 rounded-xl px-4 py-2.5 text-[13px] font-mono focus:outline-none focus:border-mauve"
                data-testid="reset-token" />
            </label>
            <label className="block">
              <div className="text-[12px] font-mono uppercase tracking-[0.14em] text-text-dim mb-1">New password</div>
              <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-surface border border-line-2 rounded-xl px-4 py-2.5 text-[14px] focus:outline-none focus:border-mauve"
                data-testid="reset-password" />
            </label>
            {msg && <div className={msg.kind === "success" ? "text-[13px] text-mauve" : "text-[13px] text-red-400"}>{msg.text}</div>}
            <Button type="submit" className="w-full justify-center" data-testid="reset-submit">Update password</Button>
          </form>
        </CardBody></Card>
        <p className="text-[12.5px] text-text-faint mt-6 text-center">
          <Link to="/login" className="text-mauve hover:underline">Back to sign in</Link>
        </p>
      </Container>
    </Section>
  );
}
